import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Home, Building2, Store, ArrowRight } from 'lucide-react';
import { heroSlides as defaultHeroSlides, cities, residentialConfigs, commercialConfigs } from '../mock';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useSiteSettings } from '../lib/settings';
import PostPropertyModal from './PostPropertyModal';

const HeroSection = () => {
  const [current, setCurrent] = useState(0);
  const [region, setRegion] = useState('INDIA');
  const [category, setCategory] = useState('Residential');
  const [selectedCity, setSelectedCity] = useState('Gurgaon');
  const [postOpen, setPostOpen] = useState(false);
  const { hero_slides } = useSiteSettings();

  // Use admin-configured slides if available, else fall back to defaults
  const heroSlides = useMemo(() => {
    if (Array.isArray(hero_slides) && hero_slides.length > 0) {
      return hero_slides.map((image, idx) => ({ id: `admin_${idx}`, image, alt: `Slide ${idx + 1}` }));
    }
    return defaultHeroSlides;
  }, [hero_slides]);

  // Reset index if slides shrink
  useEffect(() => { if (current >= heroSlides.length) setCurrent(0); }, [heroSlides.length, current]);

  useEffect(() => {
    const t = setInterval(() => setCurrent((c) => (c + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative bg-gradient-to-b from-[#fef2f4] to-white pt-6 md:pt-10 pb-8">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Carousel */}
        <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-lg bg-gray-100">
          <div className="relative aspect-[16/6] md:aspect-[16/5]">
            {heroSlides.map((s, i) => (
              <img
                key={s.id}
                src={s.image}
                alt={s.alt}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0'}`}
              />
            ))}
          </div>
          <button
            onClick={() => setCurrent((c) => (c - 1 + heroSlides.length) % heroSlides.length)}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition"
            aria-label="Prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrent((c) => (c + 1) % heroSlides.length)}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-3 md:bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-1.5 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-2 bg-white/60'}`}
              />
            ))}
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mt-10 md:mt-14">
          <h3 className="text-2xl md:text-3xl font-black text-[#9a1b2f] tracking-tight">SAFEHOUSE</h3>
          <h1 className="mt-2 text-xl md:text-3xl font-bold text-gray-800 max-w-3xl mx-auto leading-tight">
            ONE STOP SOLUTION FOR ALL YOUR REAL ESTATE NEEDS
          </h1>
          <p className="mt-3 text-sm md:text-base text-gray-600">
            Are you a property owner?{' '}
            <button
              type="button"
              onClick={() => setPostOpen(true)}
              className="text-[#9a1b2f] font-semibold underline decoration-[#9a1b2f]/30 underline-offset-4 hover:decoration-[#9a1b2f] cursor-pointer"
            >
              Sell Your Property for Free
            </button>
          </p>
        </div>

        {/* Filter Card */}
        <div className="mt-8 max-w-5xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 p-4 md:p-6">
          {/* Region tabs */}
          <div className="flex justify-center gap-6 border-b border-gray-100 mb-5">
            {['INDIA', 'OVERSEAS'].map((r) => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                className={`pb-3 text-sm font-semibold tracking-wide transition-colors relative ${region === r ? 'text-[#9a1b2f]' : 'text-gray-500 hover:text-gray-800'}`}
              >
                {r}
                {region === r && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#9a1b2f] rounded-full" />}
              </button>
            ))}
          </div>

          {/* City selector */}
          <div className="grid md:grid-cols-2 gap-3 mb-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1 mb-1.5">
                <MapPin className="w-3 h-3" /> Select City
              </label>
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="h-11 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1 mb-1.5">
                <Building2 className="w-3 h-3" /> All Developers
              </label>
              <Select defaultValue="all">
                <SelectTrigger className="h-11 border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Developers</SelectItem>
                  <SelectItem value="m3m">M3M</SelectItem>
                  <SelectItem value="dlf">DLF</SelectItem>
                  <SelectItem value="godrej">Godrej</SelectItem>
                  <SelectItem value="eldeco">Eldeco</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Category tabs */}
          <Tabs value={category} onValueChange={setCategory} className="w-full">
            <TabsList className="grid grid-cols-2 mb-5 bg-gray-100 h-11 p-1 rounded-full">
              <TabsTrigger value="Residential" className="rounded-full data-[state=active]:bg-[#9a1b2f] data-[state=active]:text-white text-sm font-semibold">
                <Home className="w-4 h-4 mr-2" /> Residential
              </TabsTrigger>
              <TabsTrigger value="Commercial" className="rounded-full data-[state=active]:bg-[#9a1b2f] data-[state=active]:text-white text-sm font-semibold">
                <Store className="w-4 h-4 mr-2" /> Commercial
              </TabsTrigger>
            </TabsList>

            <TabsContent value="Residential" className="mt-0">
              <div className="flex flex-wrap gap-2 mb-4">
                {['Resale Listing', 'Rent Listing'].map((l, i) => (
                  <button key={l} className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${i === 0 ? 'bg-[#9a1b2f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{l}</button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mb-3 text-center">Click the counts to see the listings</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {residentialConfigs.map((cfg) => (
                  <Link key={cfg.label} to={`/listings?type=resale&city=${selectedCity}`} className="group flex flex-col items-center justify-center p-3 rounded-xl border border-gray-100 hover:border-[#9a1b2f] hover:bg-[#fef2f4] transition-all">
                    <span className="text-[11px] font-semibold text-gray-600 group-hover:text-[#9a1b2f]">{cfg.label}</span>
                    <span className="text-lg font-black text-[#9a1b2f] mt-1">{cfg.count}</span>
                  </Link>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="Commercial" className="mt-0">
              <div className="flex flex-wrap gap-2 mb-4">
                {['Resale Commercial', 'Rent Commercial'].map((l, i) => (
                  <button key={l} className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors ${i === 0 ? 'bg-[#9a1b2f] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{l}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {commercialConfigs.map((cfg) => (
                  <Link key={cfg.label} to={`/listings?type=resale_commercial&city=${selectedCity}`} className="group flex flex-col items-center justify-center p-3 rounded-xl border border-gray-100 hover:border-[#9a1b2f] hover:bg-[#fef2f4] transition-all">
                    <span className="text-[11px] font-semibold text-gray-600 group-hover:text-[#9a1b2f] text-center">{cfg.label}</span>
                  </Link>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-center mt-5">
            <Link to={`/listings?type=${category === 'Commercial' ? 'resale_commercial' : 'resale'}&city=${selectedCity}`}>
              <Button className="bg-[#9a1b2f] hover:bg-[#7a1524] rounded-full px-8 h-11 font-semibold">
                Explore Properties <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <PostPropertyModal open={postOpen} onOpenChange={setPostOpen} defaultIntent="sell" />
    </section>
  );
};

export default HeroSection;
